/**
 * Centralized Error Handler for API calls
 * Provides consistent error handling across all API services
 */

/**
 * Error types enumeration
 */
export const ErrorTypes = {
  NETWORK_ERROR: 'NETWORK_ERROR',
  TIMEOUT_ERROR: 'TIMEOUT_ERROR',
  AUTH_ERROR: 'AUTH_ERROR',
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  SERVER_ERROR: 'SERVER_ERROR',
  UNKNOWN_ERROR: 'UNKNOWN_ERROR',
  CANCELLED_ERROR: 'CANCELLED_ERROR'
};

/**
 * User-friendly error messages
 */
const ErrorMessages = {
  NETWORK_ERROR: 'Network connection error. Please check your internet connection and try again.',
  TIMEOUT_ERROR: 'Request timeout. The server is taking too long to respond. Please try again.',
  AUTH_ERROR: 'Authentication failed. Please log in again.',
  VALIDATION_ERROR: 'Invalid data provided. Please check your input and try again.',
  SERVER_ERROR: 'Server error occurred. Please try again later or contact support.',
  UNKNOWN_ERROR: 'An unexpected error occurred. Please try again or contact support.',
  CANCELLED_ERROR: 'Request was cancelled.'
};

/**
 * Determines the error type from an axios error
 * @param {Error} error - The error object
 * @returns {string} Error type
 */
export const getErrorType = (error) => {
  if (!error) return ErrorTypes.UNKNOWN_ERROR;

  // Network error (no response from server)
  if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
    return ErrorTypes.TIMEOUT_ERROR;
  }

  if (error.code === 'ERR_NETWORK' || error.message?.includes('Network Error')) {
    return ErrorTypes.NETWORK_ERROR;
  }

  // Request cancelled
  if (error.code === 'ERR_CANCELED' || error.message?.includes('canceled')) {
    return ErrorTypes.CANCELLED_ERROR;
  }

  // HTTP status code errors
  if (error.response) {
    const status = error.response.status;

    // Authentication errors
    if (status === 401 || status === 403) {
      return ErrorTypes.AUTH_ERROR;
    }

    // Validation errors
    if (status === 400 || status === 422) {
      return ErrorTypes.VALIDATION_ERROR;
    }

    // Server errors
    if (status >= 500) {
      return ErrorTypes.SERVER_ERROR;
    }
  }

  return ErrorTypes.UNKNOWN_ERROR;
};

/**
 * Extracts error message from error object
 * @param {Error} error - The error object
 * @returns {string} Error message
 */
export const getErrorMessage = (error) => {
  if (!error) return ErrorMessages.UNKNOWN_ERROR;

  const errorType = getErrorType(error);

  // Try to get message from response
  if (error.response?.data) {
    const data = error.response.data;
    
    // Check for message in various formats
    if (data.message) {
      return data.message;
    }
    if (data.error) {
      return typeof data.error === 'string' ? data.error : data.error.message || ErrorMessages[errorType];
    }
    if (data.errorMessage) {
      return data.errorMessage;
    }
    if (data.msg) {
      return data.msg;
    }
  }

  // Use error message if available
  if (error.message && error.message !== 'Network Error') {
    return error.message;
  }

  // Fallback to predefined messages
  return ErrorMessages[errorType] || ErrorMessages.UNKNOWN_ERROR;
};

/**
 * Formats error response for consistent structure
 * @param {Error} error - The error object
 * @returns {Object} Formatted error object
 */
export const formatError = (error) => {
  const errorType = getErrorType(error);
  const message = getErrorMessage(error);
  const status = error.response?.status || null;
  const data = error.response?.data || null;

  return {
    error: true,
    errorType,
    message,
    status,
    data,
    originalError: error
  };
};

/**
 * Handles API errors with logging and user notification
 * @param {Error} error - The error object
 * @param {string} apiName - Name of the API that failed
 * @param {Object} options - Additional options
 * @returns {Object} Formatted error object
 */
export const handleApiError = (error, apiName = 'API', options = {}) => {
  const formattedError = formatError(error);

  // Log error for debugging
  if (process.env.NODE_ENV === 'development' || options.logError !== false) {
    console.error(`[${apiName}] Error:`, {
      type: formattedError.errorType,
      message: formattedError.message,
      status: formattedError.status,
      data: formattedError.data,
      error: error
    });
  }

  // Handle authentication errors
  if (formattedError.errorType === ErrorTypes.AUTH_ERROR) {
    // Clear local storage and redirect to login if not already there
    if (typeof window !== 'undefined' && !window.location.pathname.includes('/login')) {
      setTimeout(() => {
        localStorage.clear();
        window.location.href = '/login';
      }, 1000);
    }
  }

  return formattedError;
};

/**
 * Wraps an API call with error handling
 * @param {Promise} apiCall - The API call promise
 * @param {string} apiName - Name of the API
 * @param {Object} options - Additional options
 * @returns {Promise} Wrapped promise with error handling
 */
export const withErrorHandling = async (apiCall, apiName = 'API', options = {}) => {
  try {
    const response = await apiCall;
    return response;
  } catch (error) {
    const formattedError = handleApiError(error, apiName, options);
    
    // Re-throw formatted error if throwError is true (default: true)
    if (options.throwError !== false) {
      throw formattedError;
    }
    
    // Return error object if throwError is false
    return {
      data: null,
      error: formattedError
    };
  }
};

export default {
  ErrorTypes,
  getErrorType,
  getErrorMessage,
  formatError,
  handleApiError,
  withErrorHandling
};

